import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Lock, MessageSquare, Send } from '@/components/icons';
import { COMMENT_MAX_LENGTH } from '@1qa/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatDateTime, timeAgo } from '@/lib/format';
import type { CommentThreadView } from '@/lib/types';
import { Badge, Button, Card, CardHeader, Textarea, useToast } from '@/components/ui';

function Thread({ thread, runId }: { thread: CommentThreadView; runId: string }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState('');
  const iAmViewer = thread.viewer.id === user?.id;
  const other = iAmViewer ? thread.reporter : thread.viewer;

  const send = useMutation({
    mutationFn: () => api.addComment(thread.shareId, draft.trim()),
    onSuccess: () => {
      setDraft('');
      qc.invalidateQueries({ queryKey: ['comments', runId] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (draft.trim()) send.mutate();
  };

  return (
    <Card className="no-print">
      <CardHeader
        title={
          <span className="inline-flex flex-wrap items-center gap-2">
            <MessageSquare className="size-4 text-brand-500" />
            {iAmViewer ? `Comments to ${other.name}` : `Comments from ${other.name}`}
            {thread.unread > 0 && <Badge tone="brand">{thread.unread} new</Badge>}
          </span>
        }
        description={
          <span className="inline-flex items-center gap-1.5">
            <Lock className="size-3.5" />
            {iAmViewer
              ? `${other.name} shared this report with you. Only the two of you see these comments.`
              : `You shared this report with ${other.name} (${other.email}). Only the two of you see this conversation.`}
          </span>
        }
      />
      <div className="space-y-3 px-5 py-4">
        {thread.comments.length === 0 ? (
          <p className="text-sm text-slate-500">
            {iAmViewer ? `Noticed something? Tell ${other.name} here.` : `No comments from ${other.name} yet.`}
          </p>
        ) : (
          <ol className="space-y-3" aria-label="Comments">
            {thread.comments.map((c) => {
              const mine = c.author.id === user?.id;
              return (
                <li key={c.id} className={clsx('flex', mine ? 'justify-end' : 'justify-start')}>
                  <div
                    className={clsx(
                      'max-w-[85%] rounded-2xl px-4 py-2.5 text-sm',
                      mine ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100',
                    )}
                  >
                    <p className="break-words whitespace-pre-wrap">{c.body}</p>
                    <p className={clsx('mt-1 text-[11px]', mine ? 'text-white/75' : 'text-slate-500 dark:text-slate-400')} title={formatDateTime(c.createdAt)}>
                      {mine ? 'You' : c.author.name} · {timeAgo(c.createdAt)}
                      {mine && c.readAt && ' · seen'}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
        <form onSubmit={submit} className="flex items-end gap-2 pt-2">
          <Textarea
            aria-label={`Comment to ${other.name}`}
            placeholder={iAmViewer ? `Write a comment to ${other.name}…` : `Reply to ${other.name}…`}
            value={draft}
            rows={2}
            maxLength={COMMENT_MAX_LENGTH}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(e);
            }}
          />
          <Button type="submit" loading={send.isPending} disabled={!draft.trim()} icon={<Send className="size-4" />}>
            Send
          </Button>
        </form>
      </div>
    </Card>
  );
}

/**
 * Private comment threads on a report: a View Only user (or anyone the report was shared with)
 * writes to the member who shared it. Each person only sees their own conversations.
 */
export function CommentsPanel({ runId }: { runId: string }) {
  const qc = useQueryClient();
  const threads = useQuery({ queryKey: ['comments', runId], queryFn: () => api.comments(runId), refetchInterval: 30_000 });

  // Opening the threads marks them read on the server; refresh the sidebar badge.
  useEffect(() => {
    if (threads.data) qc.invalidateQueries({ queryKey: ['comments-unread'] });
  }, [threads.data, qc]);

  if (!threads.data || threads.data.length === 0) return null;
  return (
    <section aria-label="Comments" className="space-y-4">
      {threads.data.map((t) => (
        <Thread key={t.shareId} thread={t} runId={runId} />
      ))}
    </section>
  );
}
